import React from 'react';
import Button from '@material-ui/core/Button';
import Link from '@material-ui/core/Link';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Icon from '@material-ui/core/Icon';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import GoogleMapReact from 'google-map-react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { RouteComponentProps, match } from 'react-router-dom';
// Components
import { ActionLogs } from '../../../components/action-logs';
import { Anchor } from '../../../components/anchor';
import { AnchorBlock } from '../../../components/anchorBlock';
import App from '../../../components/app';
import { ActionFunction, ListCell, FormatType } from '../../../local-types';
import ErrorMessage from '../../../components/error-message';
import EnhancedListItem from '../../../components/listItem';
import MyLink from '../../../components/mylink';
import { TooltipButton } from '../../../components/tooltip-button';
import { Typobold } from '../../../components/typobold';
import MainLoading from '../../../components/main-loading';
import Listing from '../../../components/listing';
import { Dialog } from '../../../components/dialog';
import BookingBillView from '../bill';
// Modules
import { actions as billActions } from '../../../redux/reducers/bills';
import {
    actions as bookingActions,
    BookingById,
    selectors as bookingSelectors,
    types as bookingTypes,
} from '../../../redux/reducers/bookings';
import { actions as errorActions, ErrorPayload, selectors as errorSelectors } from '../../../redux/reducers/errors';
import { BookingModule, BookingStatus, TParams, Vehicle } from '../../../redux/reducers/api-types';
// Ressources
import AddressUtils from '../../../utils/addressUtils';
import DateUtils from '../../../utils/dateUtils';
import FormattingUtils from '../../../utils/formattingUtils';
import MapUtils from '../../../utils/mapUtils';
import UserUtils, { AccountRight } from '../../../utils/userUtils';
import withGlobalStyles from '../../../utils/global-styles';
import styles from './booking-view-style';
import { createPath, paths } from '../../paths';
import ChartsUtils from '../../../utils/chartsUtils';
import StringUtils from '../../../utils/stringUtils';
import { getCostCenterDetails, getVehicleDetails } from '../../../utils/businessUtils';
import { clsx } from '../../../utils/htmlUtils';
import envConfig from '../../../config';

// GPS positions report the speed in m/s, the speed graph displays km/h.
const MS_TO_KMH = 3.6;

interface BookingViewPageProps extends WithTranslation, WithStyles, RouteComponentProps {
    match: match<TParams>;
    globalClasses: any;
    center: any;
    zoom: any;
    bookings: BookingById;
    getBooking: (id: string, callback?) => any;
    getBookingRideManager: (uuid: string) => any;
    getRidePositionsHistory: (uuid: string, callback?) => any;
    addTollFee: (id: string, payload) => any;
    reloadBill: (id: string, payload) => any;
    cancelBookingCallCenter: (payload: any, callback) => any;
    getBookingError: ErrorPayload;
    clearError: (errorType: string) => void;
    cancelBookingCallCenterError: ErrorPayload;
}

interface BookingViewPageState {
    approches: any[];
    attentes: any[];
    courses: any[];
    // Points fetched from the whereru positions history.
    // null means "use the booking payload points" (legacy source).
    historyPoints: any[] | null;
    // Becomes true once the positions-history fetch has settled (resolved or skipped),
    // so we only render the map once the point source is decided.
    pointsReady: boolean;
}

class BookingViewPage extends React.Component<BookingViewPageProps, BookingViewPageState> {
    speedOptions: Highcharts.Options = null;
    routes: { [key: number]: { lat: number; lng: number } } = {};
    marker = null;
    fixMarkers: boolean = false;
    _courses = [];
    _approches = [];
    _attentes = [];

    static defaultProps = {
        center: MapUtils.defaultCenter,
        zoom: MapUtils.defaultZoom,
    };

    constructor(props: BookingViewPageProps) {
        super(props);
        this.state = {
            approches: [],
            attentes: [],
            courses: [],
            historyPoints: null,
            pointsReady: false,
        };
    }

    componentDidMount() {
        this.load();
    }

    load = (): void => {
        // A failure recorded for the entity we come from would otherwise greet the next
        // one with the retry panel instead of the loader.
        this.props.clearError(bookingTypes.GET_BOOKING);

        const { match, bookings } = this.props;
        const id = match.params.id;
        const rider = bookings[id];
        if (!rider) {
            this.props.getBooking(id, (_, data) => {
                if (data?.payload?.ride_uuid) {
                    this.props.getBookingRideManager(data.payload.ride_uuid);
                }
                this.loadHistoryPoints(data?.payload?.ride_uuid);
            });
        } else {
            this.loadHistoryPoints(rider.ride_uuid);
        }
    };

    // Try to fetch the ride positions from the whereru history service. If it returns points,
    // they take precedence; otherwise we fall back to the booking payload points.
    loadHistoryPoints = (rideUuid?: string) => {
        if (!rideUuid) {
            this.setState({ pointsReady: true });
            return;
        }
        this.props.getRidePositionsHistory(rideUuid, (payload) => {
            const points = payload?.points;
            this.setState({
                historyPoints: points && points.length > 0 ? points : null,
                pointsReady: true,
            });
        });
    };

    getBounds = (payload, maps) => {
        const bounds = new maps.LatLngBounds();
        if (payload.pickup_location && MapUtils.checkLocation(payload.pickup_location)) {
            bounds.extend(new maps.LatLng(payload.pickup_location.lat, payload.pickup_location.lng));
        }
        if (payload.dropoff_location && MapUtils.checkLocation(payload.dropoff_location)) {
            bounds.extend(new maps.LatLng(payload.dropoff_location.lat, payload.dropoff_location.lng));
        }
        if (payload.planned_dropoff_location && MapUtils.checkLocation(payload.planned_dropoff_location)) {
            bounds.extend(new maps.LatLng(payload.planned_dropoff_location.lat, payload.planned_dropoff_location.lng));
        }
        return bounds;
    };

    sortApiPoints = (payload, points, bounds, maps) => {
        if (points) {
            let change = false;
            let changeTwo = false;
            points.forEach((p) => {
                bounds.extend(new maps.LatLng(p.lat, p.lng));
                const dateMs = p.date * 1000;
                this.routes[dateMs] = { lat: p.lat, lng: p.lng };

                if (p.speed !== null) {
                    const speed = Math.round(p.speed * MS_TO_KMH);
                    const sub = [];
                    sub.push(dateMs);
                    sub.push(speed);
                    if (!payload.driver_arrival_date || p.date < payload.driver_arrival_date) {
                        this._approches.push(sub);
                    } else if (
                        p.date >= payload.driver_arrival_date &&
                        (!payload.pickup_date || p.date < payload.pickup_date)
                    ) {
                        if (!change && payload.driver_arrival_date) {
                            const pickupDateMs = payload.driver_arrival_date * 1000;
                            this._approches.push([pickupDateMs, speed]);
                            this._attentes.push([pickupDateMs, speed]);
                            this.routes[pickupDateMs] = this.routes[dateMs];
                            change = true;
                        }
                        this._attentes.push(sub);
                    } else {
                        if (!changeTwo && payload.pickup_date) {
                            const pickupDateMs = payload.pickup_date * 1000;
                            if (this._attentes.length > 0) {
                                this._attentes.push([pickupDateMs, speed]);
                            } else {
                                this._approches.push([pickupDateMs, speed]);
                            }
                            this._courses.push([pickupDateMs, speed]);
                            this.routes[pickupDateMs] = this.routes[dateMs];
                            changeTwo = true;
                        }
                        this._courses.push(sub);
                    }
                }
            });
        }
    };

    getPickupPoints = (payload, points) => {
        return payload.driver_arrival_date ? points.filter((p) => p.date <= payload.driver_arrival_date) : points;
    };

    getWaitPoints = (payload, points) => {
        return payload.pickup_date
            ? points.filter((p) => p.date > payload.driver_arrival_date && p.date <= payload.pickup_date)
            : payload.driver_arrival_date
              ? points.filter((p) => p.date > payload.driver_arrival_date)
              : [];
    };

    getDriverPoints = (payload, points) => {
        return payload.pickup_date ? points.filter((p) => p.date >= payload.pickup_date) : [];
    };

    pickupPolyline = (maps, pickupPoints) => {
        return new maps.Polyline({
            path: pickupPoints,
            strokeColor: 'black',
            strokeOpacity: 1.0,
            strokeWeight: 5,
        });
    };

    waithPolyline = (maps, waitPoints) => {
        return new maps.Polyline({
            path: waitPoints,
            strokeColor: 'orange',
            strokeOpacity: 1.0,
            strokeWeight: 5,
        });
    };

    driverPolyline = (maps, drivePoints) => {
        return new maps.Polyline({
            path: drivePoints,
            strokeColor: 'blue',
            strokeOpacity: 1.0,
            strokeWeight: 5,
        });
    };

    loadSpeedGraph = (map, maps) => {
        const { t } = this.props;
        const bookings_per_app: Highcharts.SeriesOptionsType[] = [
            { name: t('graph.approach'), type: 'line', data: this._approches },
            { name: t('graph.wait'), type: 'line', data: this._attentes },
            { name: t('graph.drive'), type: 'line', data: this._courses },
        ];

        this.speedOptions = ChartsUtils.createSpeedChart(t, bookings_per_app, this.routes, this.showOnMap, map, maps);
    };

    handleApiLoaded = (map, maps): void => {
        const { t, match, bookings } = this.props;
        const { historyPoints } = this.state;
        const id = match.params.id;
        const payload = bookings[id];

        // Prefer the whereru history points over the legacy booking payload points.
        // Both sources report the speed in m/s, converted to km/h for the graph.
        const points = historyPoints ?? payload.points;

        if (map && maps) {
            const bounds = this.getBounds(payload, maps);
            map.fitBounds(bounds);
            this.sortApiPoints(payload, points, bounds, maps);

            if (points) {
                const pickupPoints = this.getPickupPoints(payload, points);
                const waitPoints = this.getWaitPoints(payload, points);
                const drivePoints = this.getDriverPoints(payload, points);

                if (waitPoints.length > 0) {
                    pickupPoints.push(waitPoints[0]);
                }
                if (drivePoints.length > 0) {
                    waitPoints.push(drivePoints[0]);
                }

                const pickupPath = this.pickupPolyline(maps, pickupPoints);
                const waitPath = this.waithPolyline(maps, waitPoints);
                const drivePath = this.driverPolyline(maps, drivePoints);

                pickupPath.setMap(map);
                waitPath.setMap(map);
                drivePath.setMap(map);
            }

            //Load Speed Graph
            this.loadSpeedGraph(map, maps);

            if (!this.fixMarkers) {
                this.fixMarkers = true;

                if (payload.dropoff_location) {
                    MapUtils.addMarker(maps, map, payload.dropoff_location, t('graph.goal'), 'G');
                }
                if (payload.pickup_location) {
                    MapUtils.addMarker(maps, map, payload.pickup_location, t('graph.pick'), 'P');
                }
                if (payload.planned_dropoff_location) {
                    MapUtils.addMarker(maps, map, payload.planned_dropoff_location, t('graph.dropoff'), 'D');
                }
                if (points && points[0]) {
                    MapUtils.addMarker(maps, map, points[0], t('graph.car'), 'C');
                }
            }

            this.setState({
                approches: this._approches,
                attentes: this._attentes,
                courses: this._courses,
            });
        }
    };

    handleTollFee(bill, booking, fields) {
        const { price } = fields;

        this.props.addTollFee(bill.id, { amount: price, booking_id: booking.id });
    }

    handleRefresh = (bill, booking) => (e) => {
        this.props.reloadBill(bill.id, { booking_id: booking.id });
    };

    handleGoToRefund = (id) => (e) => {
        this.props.history.push(createPath(paths.bookingsRefund, { id }));
    };

    handleCancelBooking = () => {
        const { match, bookings } = this.props;
        const id = match.params.id;
        const payload: BookingModule = bookings[id];

        // Call with potential Charge
        this.props.cancelBookingCallCenter({ uuid: payload.uuid, phonenumber: payload.booker?.phone_number }, () => {
            this.props.getBooking(id);
        });
    };

    showOnMap = (el, map, maps) => {
        const t = Math.floor(el.target.x / 1000) * 1000;
        const p = this.routes[t];

        if (!this.marker) {
            this.marker = new maps.Marker({
                position: p,
                map: map,
            });
        } else {
            this.marker.setPosition(p);
        }
    };

    initApprocheTime = (payload: BookingModule) => {
        if (payload?.initial_distance?.time) {
            return Math.ceil(payload.initial_distance.time / 60);
        }
        return null;
    };

    getApprocheTime = (payload: BookingModule): number => {
        let approachTime = null;
        if (payload?.driver_arrival_date) {
            approachTime = Math.floor((payload.driver_arrival_date - payload.acceptance_date) / 60);
        }

        if (payload?.pickup_date && !payload.driver_arrival_date) {
            approachTime = Math.floor((payload.pickup_date - payload.acceptance_date) / 60);
        }
        return approachTime;
    };

    listItemBuilder = (payload: BookingModule, initApproachTime: number, approachTime: number): ListCell[] => {
        const { t } = this.props;
        return [
            { title: t('common:form.number'), data: payload.number },
            { title: t('common:form.id'), data: payload.id },
            {
                title: t('rider'),
                data: (
                    <MyLink color="secondary" to={`${paths.riders}/${payload.rider.id}`}>
                        {payload.rider.fullname}
                    </MyLink>
                ),
            },
            {
                title: t('booker'),
                data: payload.booker ? (
                    <MyLink color="secondary" to={`${paths.riders}/${payload.booker.id}`}>
                        {payload.booker.fullname}
                    </MyLink>
                ) : (
                    <MyLink color="secondary" to={`${paths.riders}/${payload.rider.id}`}>
                        {payload.rider.fullname}
                    </MyLink>
                ),
            },
            {
                title: t('reservation'),
                data: payload.reservation && (
                    <MyLink color="secondary" to={`${paths.reservations}/${payload.reservation.id}`}>
                        {payload.reservation.id}
                    </MyLink>
                ),
                optional: true,
            },
            { title: t('dispatch_group'), data: payload.dispatch_group.name },
            { title: t('source'), data: payload.details?.source },
            {
                title: t('company'),
                data: payload.company && (
                    <MyLink color="secondary" to={`${paths.companies}/${payload.company.id}`}>
                        {payload.company.name}
                    </MyLink>
                ),
                optional: true,
            },
            {
                title: t('subcontract'),
                data: payload.subcontract?.uber_id
                    ? `Uber: ${payload.subcontract?.uber_id}`
                    : payload.subcontract?.lecab_id
                      ? `LeCab: ${payload.subcontract?.lecab_id}`
                      : '',
                optional: true,
            },
            { title: t('external_reference'), data: payload.reference, optional: true },
            {
                title: t('cost_center'),
                data: getCostCenterDetails(payload.cost_center),
            },
            {
                title: t('reservation:ride.title'),
                data: payload.reservation?.provision?.duration
                    ? `${t('reservation:ride.mad')} ${t('common:hour_from')} ${
                          parseFloat((payload.reservation?.provision.duration / 60).toFixed(2)) + ' H'
                      }`
                    : payload.planned_dropoff_location
                      ? t('reservation:ride.normal')
                      : t('reservation:ride.free'),
            },
            { title: t('ride_reason'), data: payload.reason },
            {
                title: t('driver'),
                data: payload.driver && (
                    <Link color="secondary" href={`${paths.drivers}/${payload.driver.id}`}>
                        {payload.driver.fullname}
                    </Link>
                ),
            },
            {
                title: t('vehicle'),
                data: payload.vehicle && (
                    <MyLink color="secondary" to={`${paths.vehicles}/${payload.vehicle.id}`}>
                        {getVehicleDetails(payload?.vehicle as Vehicle)}
                    </MyLink>
                ),
            },
            {
                title: t('pickup_location'),
                data:
                    payload.pickup_location &&
                    `${AddressUtils.objectToAddress(
                        payload.pickup_location.address,
                    )}${AddressUtils.tripConnectionToString(payload.trip_connection, !!payload.meeting_point)}`,
            },
            {
                title: t('planned_dropoff_location'),
                data:
                    payload.planned_dropoff_location &&
                    AddressUtils.objectToAddress(payload.planned_dropoff_location.address),
            },
            {
                title: t('meeting_point'),
                data:
                    payload.meeting_point &&
                    AddressUtils.meetingPointToString(payload.meeting_point, payload.nameboard_option),
            },
            {
                title: t('dropoff_location'),
                data: payload.dropoff_location && AddressUtils.objectToAddress(payload.dropoff_location.address),
                optional: true,
            },
            {
                title: t('time_to_destination'),
                data: parseFloat((payload.time_to_destination / 60).toFixed(2)) + ' min',
                optional: true,
            },
            {
                title: t('distance_to_destination'),
                data: parseFloat((payload.distance_to_destination / 1000).toFixed(2)) + ' km',
                optional: true,
            },
            {
                title: t('time_total_end'),
                data:
                    payload.dropoff_date &&
                    payload.pickup_date &&
                    DateUtils.getBiggestDiff(
                        t,
                        DateUtils.dateDiff(payload.pickup_date * 1000, payload.dropoff_date * 1000),
                    ),
                optional: true,
            },
            {
                title: t('distance_total_end'),
                data: payload.ride_billing?.distance && parseFloat((payload.ride_billing?.distance).toFixed(2)) + ' km',
                optional: true,
            },
            {
                title: t('nameboard_option'),
                data: payload.nameboard_option,
                type: FormatType.Boolean,
            },
            {
                title: t('order'),
                data:
                    payload.order &&
                    `${FormattingUtils.price(payload.order.base_price_without_vat)} HT (${FormattingUtils.price(
                        payload.order.base_price_with_vat,
                    )} TTC)`,
                optional: true,
            },
            // {
            //     title: t('partner'),
            //     data:
            //         payload.ride_billing &&
            //         `${FormattingUtils.price(payload.ride_billing.pre_tax_partner)} HT (${FormattingUtils.price(
            //             payload.ride_billing.with_tax_partner,
            //         )} TTC)`,
            //     optional: true,
            // },
            {
                title: t('fixed_rate'),
                data:
                    payload.order && !payload.fixed_rate ? t('fixed_rate_cancelled_yes') : t('fixed_rate_cancelled_no'),
                optional: true,
            },
            {
                title: t('driver_additional_info'),
                data: payload.driver_additional_info,
                optional: true,
            },
            {
                title: t('creation_date'),
                data: payload.creation_date,
                type: FormatType.DateLong,
            },
            {
                title: t('reservation_date'),
                data: payload.reservation?.planned_pickup_date,
                type: FormatType.DateLong,
                optional: true,
            },
            {
                title: t('acceptance_date'),
                data: payload.acceptance_date,
                type: FormatType.DateLong,
            },
            {
                title: t('driver_arrival_date'),
                data: payload.driver_arrival_date,
                type: FormatType.DateLong,
                optional: true,
            },
            {
                title: t('pickup_date'),
                data: payload.pickup_date,
                type: FormatType.DateLong,
                optional: true,
            },
            {
                title: t('dropoff_date'),
                data: payload.dropoff_date,
                type: FormatType.DateLong,
                optional: true,
            },
            {
                title: t('cancellation_date'),
                data: payload.cancellation_date,
                type: FormatType.DateLong,
                optional: true,
            },
            {
                title: t('cancellation_reason'),
                data:
                    payload.cancellation_reason &&
                    `${t(payload.cancellation_reason)} ${
                        payload.cancellation_description ? '(' + payload.cancellation_description + ')' : ''
                    }`,
                optional: true,
            },
            {
                title: t('cancellation_rider'),
                data: payload?.cancel_reason_rider?.label,
                optional: true,
            },
            {
                title: t('initial_distance'),
                data: initApproachTime && `${initApproachTime} ${t('common:minutes')}`,
                optional: true,
            },
            {
                title: t('real_distance'),
                data: approachTime && `${approachTime} ${t('common:minutes')}`,
                optional: true,
            },
            {
                title: t('rider_feedback'),
                data: payload.rider_feedback?.text,
                optional: true,
            },
            {
                title: t('driver_rating'),
                data: payload.driver_rating && payload.driver_rating !== -1 ? `${payload.driver_rating} / 5` : null,
                optional: true,
            },
            {
                title: t('tip'),
                data: payload?.products_billing?.extra_tip
                    ? `${payload?.products_billing.extra_tip.with_tax_partner} €`
                    : null,
                optional: true,
            },
        ];
    };

    renderRejectionItemsView = (payload: BookingModule): JSX.Element[] => {
        const { classes, t } = this.props;
        return payload.rejections.map((item, key) => (
            <ListItem key={key}>
                <ListItemText>
                    {StringUtils.userFullname(item.driver as any) + ' - ' + DateUtils.tsToDate(item.date)}
                </ListItemText>
                <ListItemText className={classes.partnerInfoLabel}>{t(`rejection_reason_${item.type}`)}</ListItemText>
            </ListItem>
        ));
    };

    renderOperationItemView = (payload: BookingModule): JSX.Element[] => {
        const { classes } = this.props;
        const operatorLogs = [...payload.operator_logs, ...(payload.reservation?.operator_logs ?? [])];
        return operatorLogs.map((item, key) => (
            <ListItem key={key}>
                <ListItemText>{item.sender.fullname + ' - ' + DateUtils.tsToDate(item.date)}</ListItemText>
                <ListItemText className={classes.partnerInfoLabel}>{item.text}</ListItemText>
            </ListItem>
        ));
    };

    renderRegulationItemsView = (payload: BookingModule): JSX.Element => {
        const { t } = this.props;
        return (
            <Listing
                datas={payload.regulations_billing?.map((item, key) => (
                    <div key={key}>
                        <div>
                            {DateUtils.stringToDate(item.created_at)} - {item.author ? `${item.author.name} - ` : ''}
                            {item.label}
                        </div>
                        {item.with_tax_customer !== 0 && <div>Client : {item.with_tax_customer} € (TTC)</div>}
                        {item.with_tax_partner !== 0 && (
                            <div>
                                {item.partner?.name || 'Partenaire'} : {item.with_tax_partner} € (TTC)
                            </div>
                        )}
                        <div>{t(`booking:regulation_type.${item.product_type}`)}</div>
                    </div>
                ))}
                customEmpty={t('partner_bill:regulation.none')}
            />
        );
    };

    renderMessageItemsView = (payload: BookingModule): JSX.Element[] => {
        const { classes } = this.props;
        return payload.messages.map((item, key) => (
            <ListItem key={key}>
                <ListItemText>
                    {StringUtils.userFullname(item.sender) + ' - ' + DateUtils.tsToDate(item.date)}
                </ListItemText>
                <ListItemText className={classes.partnerInfoLabel}>{item.text}</ListItemText>
            </ListItem>
        ));
    };

    renderRideDetailsView = (payload: BookingModule): JSX.Element => {
        const { t, classes } = this.props;
        const { _approches, _courses } = this;

        // Get last point of _approches and first point of _courses
        const infos = [];
        if (_approches.length > 0) {
            const lastApproche = _approches[_approches.length - 1];
            infos.push({
                label: t('driver_arrival_date'),
                date: lastApproche[0],
                lat: this.routes[lastApproche[0]]?.lat,
                lng: this.routes[lastApproche[0]]?.lng,
            });
        }
        if (_courses.length > 0) {
            const firstCourse = _courses[0];
            infos.push({
                label: t('pickup_date'),
                date: firstCourse[0],
                lat: this.routes[firstCourse[0]]?.lat,
                lng: this.routes[firstCourse[0]]?.lng,
            });
        }

        return (
            <div style={{ backgroundColor: 'white' }}>
                {infos.map((item, key) => (
                    <ListItem key={key}>
                        <ListItemText primary={item.label} />
                        <ListItemText className={classes.partnerInfoLabel}>
                            {DateUtils.tsToDate(item.date / 1000)}
                            {item.lat && item.lng && (
                                <span style={{ marginLeft: 8, color: '#888', fontSize: 12 }}>
                                    ({item.lat.toFixed(7)}, {item.lng.toFixed(7)})
                                </span>
                            )}
                        </ListItemText>
                    </ListItem>
                ))}
            </div>
        );
    };

    getActions = (payload: BookingModule, id: string, isRefundEnable: boolean): ActionFunction[] => {
        const { t } = this.props;
        const actions: ActionFunction[] = [];
        if (payload) {
            if (isRefundEnable && UserUtils.checkRightsOrAdmin(AccountRight.RIDE_ACTION_ADJUSTMENT)) {
                actions.push({
                    label: t('refound.btn'),
                    func: this.handleGoToRefund(id),
                });
            }
        }
        return actions;
    };

    render() {
        const { t, classes, globalClasses, match, bookings, getBookingError, cancelBookingCallCenterError } =
            this.props;
        const id = match.params.id;
        const payload = bookings[id];

        const initApproachTime = this.initApprocheTime(payload);
        const approachTime = this.getApprocheTime(payload);

        const isRefundEnable = payload?.ride_billing?.billing_status === 'issued';
        const canCreateAdjustment: boolean = UserUtils.checkRights(AccountRight.RIDE_ACTION_ADJUSTMENT);
        const canViewBilling: boolean = UserUtils.checkRights(AccountRight.RIDER_BILLING_MGMT);

        //const bills = payload.bills.concat(payload.grouped_bills);

        return (
            <App
                needRights={AccountRight.RIDE_DETAILS}
                loading={!payload}
                loadingError={this.props.getBookingError}
                onRetry={this.load}
                header={{
                    icon: 'default',
                    title: t('title'),
                    subtitle: payload && `ID: ${payload.id}`,
                    bubble: {
                        visible: true,
                        text: payload?.status ? t('booking_status_' + payload.status) : null,
                        colorEnable: payload?.status && !payload.status.includes('cancel'),
                    },
                    actions: this.getActions(payload, id, isRefundEnable),
                    anchors: [
                        { href: '#general_infos', label: t('common:form.general_infos') },
                        { href: '#billing', label: t('billing') },
                        { href: '#operator_logs', label: t('operator_logs') },
                        { href: '#speed_graph', label: t('speed') },
                        { href: '#messages', label: t('messages') },
                        { href: '#rejections', label: t('rejections') },
                        { href: '#regulations', label: t('regulations') },
                        { href: '#history', label: t('common:history') },
                    ],
                    button: payload &&
                        UserUtils.checkRights(AccountRight.RIDE_ACTION_CANCEL) &&
                        (payload.status === BookingStatus.CREATED ||
                            payload.status === BookingStatus.DRIVER_ARRIVED ||
                            payload.status === BookingStatus.ACCEPTED) && (
                            <Dialog
                                btnTitle={t('common:validate')}
                                description={t('common:cancelParam', { id })}
                                customButton={
                                    <TooltipButton title={t('common:cancel')}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="primary"
                                            style={{ padding: 6 }}
                                            startIcon={<Icon fontSize="small">cancel</Icon>}
                                            className={clsx(globalClasses.btnAutoWidth, globalClasses.btnDanger)}
                                        >
                                            {t('common:cancel')}
                                        </Button>
                                    </TooltipButton>
                                }
                                onOK={this.handleCancelBooking}
                            />
                        ),
                }}
            >
                <ErrorMessage payload={getBookingError || cancelBookingCallCenterError} />
                {payload && (
                    <>
                        <Anchor id="general_infos" />
                        <div className={classes.root}>
                            <EnhancedListItem
                                contentProps={{ style: { flex: 1 } }}
                                datas={this.listItemBuilder(payload, initApproachTime, approachTime)}
                                title={t('common:form.general_infos')}
                                lock
                            />
                            <div style={{ flex: 1, flexDirection: 'column', marginLeft: 16 }}>
                                {canViewBilling && (
                                    <>
                                        <br />
                                        <Anchor id="billing" />
                                        <Typobold>{t('billing')}</Typobold>
                                        <BookingBillView booking={payload} />
                                    </>
                                )}
                                <AnchorBlock
                                    id="regulations"
                                    title={t('regulations')}
                                    noValue={t('regulations_none')}
                                    items={this.renderRegulationItemsView(payload)}
                                    action={
                                        <TooltipButton
                                            title={t(
                                                !canCreateAdjustment
                                                    ? 'common:rights.noAction'
                                                    : `refound.btn${isRefundEnable ? '' : 'KO'}`,
                                            )}
                                        >
                                            <div style={{ width: 'fit-content' }}>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="secondary"
                                                    style={{
                                                        alignSelf: 'flex-start',
                                                        ...(isRefundEnable && canCreateAdjustment
                                                            ? {}
                                                            : { pointerEvents: 'auto' }),
                                                    }}
                                                    disabled={!isRefundEnable || !canCreateAdjustment}
                                                    onClick={this.handleGoToRefund(id)}
                                                >
                                                    <AddIcon />
                                                    {t('refound.btn')}
                                                </Button>
                                            </div>
                                        </TooltipButton>
                                    }
                                />
                                <AnchorBlock
                                    id="operator_logs"
                                    title={t('operator_logs')}
                                    items={this.renderOperationItemView(payload)}
                                />
                            </div>
                        </div>
                        <br />
                        <Anchor id="speed_graph" />
                        {this.speedOptions ? (
                            <>
                                <HighchartsReact highcharts={Highcharts} options={this.speedOptions} />
                                {this.renderRideDetailsView(payload)}
                            </>
                        ) : (
                            <MainLoading center message={t('common:loading')} />
                        )}
                        <br />
                        <div style={{ height: '50vh', width: '100%' }}>
                            {this.state.pointsReady && (
                                <GoogleMapReact
                                    bootstrapURLKeys={{ key: envConfig.google_key }}
                                    yesIWantToUseGoogleMapApiInternals
                                    onGoogleApiLoaded={({ map, maps }) => this.handleApiLoaded(map, maps)}
                                    defaultCenter={MapUtils.defaultCenter}
                                    defaultZoom={MapUtils.defaultZoom}
                                />
                            )}
                        </div>
                        <AnchorBlock id="messages" title={t('messages')} items={this.renderMessageItemsView(payload)} />
                        <AnchorBlock
                            id="rejections"
                            title={t('rejections')}
                            items={this.renderRejectionItemsView(payload)}
                        />
                        <br />
                        <ActionLogs history={payload.action_logs} withValues />
                    </>
                )}
            </App>
        );
    }
}

const BookingViewPageWithStyleAndTranslation = withGlobalStyles(
    withStyles(styles)(withTranslation(['booking', 'bill', 'partner_bill', 'reservation', 'common'])(BookingViewPage)),
);

const mapStateToProps = (state) => ({
    bookings: bookingSelectors.bookings(state),
    getBookingError: errorSelectors.getBookingError(state),
    cancelBookingCallCenterError: errorSelectors.cancelBookingCallCenterError(state),
});

const mapDispatchToProps = {
    clearError: errorActions.clearError,
    reloadBill: billActions.reloadBill,
    getBooking: bookingActions.getBooking,
    getBookingRideManager: bookingActions.getBookingRideManager,
    getRidePositionsHistory: bookingActions.getRidePositionsHistory,
    addTollFee: bookingActions.addTollFee,
    cancelBookingCallCenter: bookingActions.cancelBookingCallCenter,
};

export default connect(mapStateToProps, mapDispatchToProps)(BookingViewPageWithStyleAndTranslation);
